import { createSignal, Show, type JSX } from "solid-js";

import { SEPARATOR_DOT } from "~/util/html/codepoint";

import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import { DeckImageFigure } from "../deck_image_figure";
import type { LibraryPageSlide as LibraryPageSlideData, PictureSrc } from "../types";

/** One page of the practice's own library filling the slide, or the part of it staff framed, with the treatments it
 *  shows and whose example it is under it, and the office's words for this patient beside that. The page is baked
 *  artwork, so nothing is drawn over it, except a video page's play button: its still shows until someone presses
 *  play, and a video is always shown whole. */
export function LibraryPageSlide(props: {
  slide: LibraryPageSlideData;
  pictureSrc: PictureSrc;
}): JSX.Element {
  const words = useDeckWords();
  const video = () => props.slide.page.video === true;
  return (
    <>
      <h2 class="sr-only">{props.slide.headline}</h2>
      <DeckImageFigure
        src={props.pictureSrc({ kind: "library_page", pageId: props.slide.page.pageId })}
        alt={props.slide.headline}
        width={props.slide.page.width}
        height={props.slide.page.height}
        // When the live route refuses the page: archived, hidden from this location, or its image gone. A page has no
        // orientation, so the pictures' "changed" wording never applies.
        failedText={words().t("pageUnavailable")}
        large
        frame={video() ? undefined : props.slide.frame}
        overlay={
          video() ? (
            // Keyed on the page, so moving from one video slide to the next shows the next one's Play button afresh.
            <Show when={props.slide.page.pageId} keyed>
              {(pageId) => (
                <LibraryVideo
                  src={props.pictureSrc({ kind: "library_video", pageId })}
                  title={props.slide.headline}
                />
              )}
            </Show>
          ) : undefined
        }
        legend={
          props.slide.words ? <p class={styles["pg-words"]}>{props.slide.words}</p> : undefined
        }
        caption={[...props.slide.treatments, props.slide.caption].join(` ${SEPARATOR_DOT} `)}
      />
    </>
  );
}

/** A play button over the still; pressed, the video plays in its place with the browser's own controls. Nothing
 *  loads or plays before that. A tap here never moves the deck (only its Back and Next, and the keys, do), and moving
 *  to another slide unmounts the player, which stops the video. `playsinline` keeps an iPhone from taking it full screen. */
function LibraryVideo(props: { src: string; title: string }): JSX.Element {
  const words = useDeckWords();
  const [playing, setPlaying] = createSignal(false);
  const [failed, setFailed] = createSignal(false);
  return (
    <Show
      when={playing()}
      fallback={
        <button
          type="button"
          class={styles["vid-play"]}
          aria-label={words().t("play", { name: props.title })}
          onClick={() => setPlaying(true)}
        >
          <span class={styles["vid-play-icon"]} aria-hidden="true" />
          <Show when={failed()}>
            <span class={styles["vid-failed"]}>{words().t("videoUnavailable")}</span>
          </Show>
        </button>
      }
    >
      <video
        // Started in the press that mounted it, so a browser that wants a gesture for sound has one. The source is set
        // here, not as an attribute, which Solid would set only after this runs: play() needs it.
        ref={(element) => {
          element.src = props.src;
          void element.play().catch(() => {});
        }}
        class={styles["vid"]}
        controls
        playsinline
        autoplay
        aria-label={props.title}
        onError={() => {
          setFailed(true);
          setPlaying(false);
        }}
      />
    </Show>
  );
}
